import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, Certificate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Award,
  Shield,
  Trophy,
  ChevronLeft,
  Share2,
  ExternalLink,
  CheckCircle,
  Download,
} from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import backgroundImage from "../assets/background-image.png";
import ithopuLogo from "../assets/ithopu-logo.png";
import ithopuText from "../assets/iTHOPU.png";

const PLATFORM_LEVEL_NAMES: Record<number, string> = {
  1: "Foundations",
  2: "Intermediate",
  3: "Advanced",
  4: "Master",
};

const TYPE_COLORS: Record<string, string> = {
  technique: "from-blue-500 to-blue-700",
  category: "from-purple-500 to-purple-700",
  platform: "from-amber-500 to-amber-700",
};

const SAMPLE_CERTIFICATE: Certificate = {
  id: "sample",
  certificate_type: "technique",
  certificate_level: null,
  title: "Vedic Multiplication Mastery",
  description: "Demonstrated mastery in Vedic Multiplication techniques",
  certificate_number: "ITHOPU-TECH-SAMPLE-2026",
  verification_hash: "sample-hash",
  accuracy: 95.0,
  problems_correct: 19,
  problems_attempted: 20,
  time_taken_seconds: 420,
  issued_at: new Date().toISOString(),
  expires_at: null,
  exam_name: "Vedic Multiplication Certification",
  exam_type: "technique",
  category: "vedic_math",
  technique_name: "Vertically & Crosswise",
};

export default function CertificateView() {
  const { certId } = useParams<{ certId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const certificateRef = useRef<HTMLDivElement>(null);

  // School logo for branded certificates
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState<string | null>(null);

  // Name fields fetched directly from DB (not relying on user-me edge function)
  const [dbFirstName, setDbFirstName] = useState<string | null>(null);
  const [dbLastName, setDbLastName] = useState<string | null>(null);
  const [nameLoaded, setNameLoaded] = useState(false);
  const [skippedNamePrompt, setSkippedNamePrompt] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const isSample = certId === "sample";
  const holderName = isSample
    ? "John Doe"
    : dbFirstName && dbLastName
      ? `${dbFirstName} ${dbLastName}`
      : dbFirstName || dbLastName || "";

  // Derive whether to show name prompt from DB-fetched name
  const showNamePrompt =
    !isSample &&
    !skippedNamePrompt &&
    !loading &&
    nameLoaded &&
    !!certificate &&
    !dbFirstName &&
    !dbLastName;

  // Fetch user's name and school info directly from DB
  useEffect(() => {
    if (isSample || !user?.id) return;

    const fetchNameAndSchool = async () => {
      try {
        const { data } = await supabase
          .from("users")
          .select("first_name, last_name, school_id")
          .eq("id", user.id)
          .single();

        if (data) {
          setDbFirstName(data.first_name || null);
          setDbLastName(data.last_name || null);

          // If user belongs to a school, fetch school logo
          if (data.school_id) {
            const { data: school } = await supabase
              .from("schools")
              .select("name, logo_url")
              .eq("id", data.school_id)
              .single();

            if (school) {
              setSchoolName(school.name || null);
              setSchoolLogoUrl(school.logo_url || null);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch name:", error);
      } finally {
        setNameLoaded(true);
      }
    };

    fetchNameAndSchool();
  }, [user?.id, isSample]);

  useEffect(() => {
    if (isSample) {
      setCertificate(SAMPLE_CERTIFICATE);
      setLoading(false);
      return;
    }

    const loadCertificate = async () => {
      try {
        const data = await api.getUserCertificates();
        const found = data.certificates.find((c) => c.id === certId);
        if (found) {
          setCertificate(found);
        }
      } catch (error) {
        console.error("Failed to load certificate:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCertificate();
  }, [certId, isSample]);

  const handleSaveName = async () => {
    if (!user || (!firstName.trim() && !lastName.trim())) return;
    setSavingName(true);
    try {
      const { error } = await supabase
        .from("users")
        .update({
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
        })
        .eq("id", user.id);

      if (error) throw error;

      // Update local state so the certificate shows the name immediately
      setDbFirstName(firstName.trim() || null);
      setDbLastName(lastName.trim() || null);
    } catch (error) {
      console.error("Failed to save name:", error);
    } finally {
      setSavingName(false);
    }
  };

  const handleShare = async () => {
    if (!certificate) return;

    const shareText = `I earned the "${certificate.title}" certification on Ithopu! Verify: ${certificate.certificate_number}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Ithopu Certificate: ${certificate.title}`,
          text: shareText,
        });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExportPDF = useCallback(async () => {
    if (!certificateRef.current || exporting) return;
    setExporting(true);

    try {
      // Wait for all fonts to be loaded before capturing
      await document.fonts.ready;

      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        onclone: (clonedDoc) => {
          // Fix bg-clip-text gradient — html2canvas doesn't support it
          // Replace with solid color text in the cloned DOM
          const gradientEls = clonedDoc.querySelectorAll(".bg-clip-text");
          gradientEls.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.backgroundClip = "unset";
            htmlEl.style.webkitBackgroundClip = "unset";
            htmlEl.style.webkitTextFillColor = "unset";
            htmlEl.style.background = "none";
            htmlEl.style.color = "#1d4ed8"; // blue-700 fallback
          });
          // Fix gradient divider lines (from-transparent via-* to-transparent)
          const gradientBgs = clonedDoc.querySelectorAll(
            "[class*='via-amber'], [class*='via-gray']",
          );
          gradientBgs.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.background = "#d1d5db"; // gray-300 solid line
          });
        },
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const x = (pdfWidth - imgWidth * ratio) / 2;
      const y = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(imgData, "PNG", x, y, imgWidth * ratio, imgHeight * ratio);
      pdf.save(
        `ithopu-certificate-${certificate?.certificate_number || "sample"}.pdf`,
      );
    } catch (error) {
      console.error("Failed to export PDF:", error);
    } finally {
      setExporting(false);
    }
  }, [exporting, certificate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="animate-pulse space-y-4">
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl text-center">
        <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Certificate not found.
        </p>
        <Button onClick={() => navigate("/certifications")}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Certifications
        </Button>
      </div>
    );
  }

  // Name prompt modal
  if (showNamePrompt) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-lg">
        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Award className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Enter Your Name
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your name will appear on the certificate. Please enter it below.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setSkippedNamePrompt(true)}
                className="flex-1"
              >
                Skip
              </Button>
              <Button
                onClick={handleSaveName}
                disabled={
                  savingName || (!firstName.trim() && !lastName.trim())
                }
                className="flex-1"
              >
                {savingName ? "Saving..." : "Save & View Certificate"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gradientClass =
    TYPE_COLORS[certificate.certificate_type] || TYPE_COLORS.technique;
  const IconComponent =
    certificate.certificate_type === "platform"
      ? Trophy
      : certificate.certificate_type === "category"
        ? Shield
        : Award;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/certifications")}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Certifications
        </Button>
        <div className="flex gap-2">
          <Button onClick={handleExportPDF} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Download PDF"}
          </Button>
        </div>
      </div>

      {isSample && (
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 text-center">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            This is a sample certificate for preview purposes only.
          </p>
        </div>
      )}

      {/* Certificate Display */}
      <div
        ref={certificateRef}
        className="relative overflow-hidden rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-white"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Semi-transparent overlay for readability */}
        <div className="absolute inset-0 bg-white/85" />

        <div className="relative z-10 p-8 md:p-12">
          {/* Header: Logo + Branding */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <img
                src={ithopuLogo}
                alt="Ithopu Logo"
                className="w-12 h-12 object-contain"
              />
              <img
                src={ithopuText}
                alt="iTHOPU"
                className="h-8 object-contain"
              />
            </div>
            <div className="flex items-center gap-3">
              {schoolLogoUrl && (
                <div className="flex items-center gap-2">
                  <img
                    src={schoolLogoUrl}
                    alt={schoolName || "School"}
                    className="h-10 object-contain"
                    crossOrigin="anonymous"
                  />
                  {schoolName && (
                    <span className="text-xs text-gray-500 font-medium max-w-[120px] truncate">
                      {schoolName}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                <CheckCircle className="w-3.5 h-3.5" />
                Valid Certificate
              </div>
            </div>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
            <IconComponent className="w-8 h-8 text-amber-500" />
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
          </div>

          {/* Certificate Title */}
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2 font-medium">
              Certificate of Achievement
            </p>
            <p className="text-sm text-gray-500 mb-4">
              This is to certify that
            </p>

            {/* Holder Name */}
            <h1
              className="text-4xl md:text-5xl font-normal text-gray-900 mb-2"
              style={{ fontFamily: "'Great Vibes', cursive" }}
            >
              {holderName || "Certificate Holder"}
            </h1>
            <div className="w-48 h-px bg-gray-300 mx-auto mb-4" />

            <p className="text-sm text-gray-500 mb-3">
              has successfully completed the certification exam for
            </p>

            {/* Certificate Subject */}
            <h2
              className={`text-2xl md:text-3xl font-bold bg-gradient-to-r ${gradientClass} bg-clip-text text-transparent mb-1`}
            >
              {certificate.title}
            </h2>
            {certificate.certificate_level && (
              <p className="text-sm text-gray-600 font-medium">
                Level {certificate.certificate_level}:{" "}
                {PLATFORM_LEVEL_NAMES[certificate.certificate_level] || ""}
              </p>
            )}
          </div>

          {/* Score Details */}
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
            <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-2xl font-bold text-blue-700">
                {certificate.accuracy.toFixed(1)}%
              </p>
              <p className="text-xs text-blue-500 font-medium">Accuracy</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-green-50 border border-green-100">
              <p className="text-2xl font-bold text-green-700">
                {certificate.problems_correct}/{certificate.problems_attempted}
              </p>
              <p className="text-xs text-green-500 font-medium">Correct</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-purple-50 border border-purple-100">
              <p className="text-2xl font-bold text-purple-700">
                {Math.floor(certificate.time_taken_seconds / 60)}m{" "}
                {Math.round(certificate.time_taken_seconds % 60)}s
              </p>
              <p className="text-xs text-purple-500 font-medium">Time</p>
            </div>
          </div>

          {/* Decorative divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          </div>

          {/* Footer Details */}
          <div className="flex flex-wrap justify-between items-end text-xs text-gray-500">
            <div className="space-y-1">
              <p>
                <span className="font-medium">Certificate No:</span>{" "}
                <span className="font-mono">
                  {certificate.certificate_number}
                </span>
              </p>
              <p>
                <span className="font-medium">Issued:</span>{" "}
                {new Date(certificate.issued_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              {certificate.category && (
                <p>
                  <span className="font-medium">Category:</span>{" "}
                  <span className="capitalize">
                    {certificate.category.replace(/_/g, " ")}
                  </span>
                </p>
              )}
            </div>
            <div className="text-right space-y-1">
              <p className="font-medium text-gray-600">
                {certificate.certificate_type.charAt(0).toUpperCase() +
                  certificate.certificate_type.slice(1)}{" "}
                Certificate
              </p>
              {certificate.expires_at && (
                <p>
                  <span className="font-medium">Expires:</span>{" "}
                  {new Date(certificate.expires_at).toLocaleDateString()}
                </p>
              )}
              <p className="text-gray-400">ithopu.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions below the certificate */}
      <div className="flex gap-3 justify-center mt-6">
        <Button onClick={handleShare} variant="outline">
          <Share2 className="w-4 h-4 mr-2" />
          {copied ? "Copied!" : "Share"}
        </Button>
        {!isSample && (
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                `/verify-certificate?number=${encodeURIComponent(certificate.certificate_number)}`,
              )
            }
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Verify
          </Button>
        )}
      </div>
    </div>
  );
}
